"use client";

import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { DayButton, DayPicker, getDefaultClassNames } from "react-day-picker";
import { cn } from "@/lib/utils";
import { Button, buttonVariants } from "@/components/ui/button";
function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = "label",
  buttonVariant = "ghost",
  formatters,
  components,
  datesWithTodos,
  ...props
}: React.ComponentProps<typeof DayPicker> & {
  buttonVariant?: React.ComponentProps<typeof Button>["variant"];
  datesWithTodos?: Set<string>;
}) {
  const defaultClassNames = getDefaultClassNames();
  return <DayPicker showOutsideDays={showOutsideDays} className={cn("bg-background group/calendar p-3 [--cell-size:--spacing(8)] [[data-slot=card-content]_&]:bg-transparent [[data-slot=popover-content]_&]:bg-transparent", String.raw`rtl:**:[.rdp-button\_next>svg]:rotate-180`, String.raw`rtl:**:[.rdp-button\_previous>svg]:rotate-180`, className)} captionLayout={captionLayout} formatters={{
    formatMonthDropdown: date => date.toLocaleString("default", {
      month: "short"
    }),
    ...formatters
  }} classNames={{
    root: cn("w-fit", defaultClassNames.root),
    months: cn("flex gap-4 flex-col md:flex-row relative", defaultClassNames.months),
    month: cn("flex flex-col w-full gap-4", defaultClassNames.month),
    nav: cn("flex items-center gap-1 w-full absolute top-0 inset-x-0 justify-between", defaultClassNames.nav),
    button_previous: cn(buttonVariants({
      variant: buttonVariant
    }), "size-(--cell-size) aria-disabled:opacity-50 p-0 select-none", defaultClassNames.button_previous),
    button_next: cn(buttonVariants({
      variant: buttonVariant
    }), "size-(--cell-size) aria-disabled:opacity-50 p-0 select-none", defaultClassNames.button_next),
    month_caption: cn("flex items-center justify-center h-(--cell-size) w-full px-(--cell-size)", defaultClassNames.month_caption),
    dropdowns: cn("w-full flex items-center text-sm font-medium justify-center h-(--cell-size) gap-1.5", defaultClassNames.dropdowns),
    dropdown_root: cn("relative has-focus:border-ring border border-input shadow-xs has-focus:ring-ring/50 has-focus:ring-[3px] rounded-md", defaultClassNames.dropdown_root),
    dropdown: cn("absolute inset-0 opacity-0", defaultClassNames.dropdown),
    caption_label: cn("select-none font-medium", captionLayout === "label" ? "text-sm" : "rounded-md pl-2 pr-1 flex items-center gap-1 text-sm h-8 [&>svg]:text-muted-foreground [&>svg]:size-3.5", defaultClassNames.caption_label),
    table: "w-full border-collapse",
    weekdays: cn("flex", defaultClassNames.weekdays),
    weekday: cn("text-muted-foreground rounded-md flex-1 font-normal text-[0.8rem] select-none", defaultClassNames.weekday),
    week: cn("flex w-full mt-2", defaultClassNames.week),
    week_number_header: cn("select-none w-(--cell-size)", defaultClassNames.week_number_header),
    week_number: cn("text-[0.8rem] select-none text-muted-foreground", defaultClassNames.week_number),
    day: cn("relative w-full h-full p-0 text-center [&:first-child[data-selected=true]_button]:rounded-l-md [&:last-child[data-selected=true]_button]:rounded-r-md group/day aspect-square select-none", defaultClassNames.day),
    range_start: cn("rounded-l-md bg-accent", defaultClassNames.range_start),
    range_middle: cn("rounded-none", defaultClassNames.range_middle),
    range_end: cn("rounded-r-md bg-accent", defaultClassNames.range_end),
    today: cn("bg-accent text-accent-foreground rounded-md data-[selected=true]:rounded-none", defaultClassNames.today),
    outside: cn("text-muted-foreground aria-selected:text-muted-foreground", defaultClassNames.outside),
    disabled: cn("text-muted-foreground opacity-50", defaultClassNames.disabled),
    hidden: cn("invisible", defaultClassNames.hidden),
    ...classNames
  }} components={{
    Root: ({
      className: className_0,
      rootRef,
      ...props_0
    }) => {
      return <div data-slot="calendar" ref={rootRef} className={cn(className_0)} {...props_0} />;
    },
    Chevron: ({
      className: className_1,
      orientation,
      ...props_1
    }) => {
      if (orientation === "left") {
        return <ChevronLeftIcon className={cn("size-4", className_1)} {...props_1} />;
      }
      if (orientation === "right") {
        return <ChevronRightIcon className={cn("size-4", className_1)} {...props_1} />;
      }
      return <ChevronDownIcon className={cn("size-4", className_1)} {...props_1} />;
    },
    DayButton: props_2 => <CalendarDayButton {...props_2} datesWithTodos={datesWithTodos} />,
    WeekNumber: ({
      children,
      ...props_3
    }) => {
      return <td {...props_3}>
              <div className="flex size-(--cell-size) items-center justify-center text-center">
                {children}
              </div>
            </td>;
    },
    ...components
  }} {...props} />;
}
function CalendarDayButton(t0) {
  const $ = _c(39);
  let className;
  let datesWithTodos;
  let day;
  let modifiers;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      day,
      modifiers,
      datesWithTodos,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = datesWithTodos;
    $[3] = day;
    $[4] = modifiers;
    $[5] = props;
  } else {
    className = $[1];
    datesWithTodos = $[2];
    day = $[3];
    modifiers = $[4];
    props = $[5];
  }
  const defaultClassNames = getDefaultClassNames();
  const ref = React.useRef(null);
  let t1;
  let t2;
  if ($[6] !== modifiers.focused) {
    t1 = () => {
      if (modifiers.focused) {
        ref.current?.focus();
      }
    };
    t2 = [modifiers.focused];
    $[6] = modifiers.focused;
    $[7] = t1;
    $[8] = t2;
  } else {
    t1 = $[7];
    t2 = $[8];
  }
  React.useEffect(t1, t2);
  let t3;
  if ($[9] !== day.date) {
    t3 = day.date.getFullYear();
    $[9] = day.date;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const year = t3;
  const t4 = String(day.date.getMonth() + 1);
  let t5;
  if ($[11] !== t4) {
    t5 = t4.padStart(2, "0");
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const month = t5;
  let t6;
  if ($[13] !== day.date) {
    t6 = day.date.getDate();
    $[13] = day.date;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const t7 = String(t6);
  let t8;
  if ($[15] !== t7) {
    t8 = t7.padStart(2, "0");
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const dayNum = t8;
  const dateString = `${year}-${month}-${dayNum}`;
  let t9;
  if ($[17] !== dateString || $[18] !== datesWithTodos) {
    t9 = datesWithTodos?.has(dateString);
    $[17] = dateString;
    $[18] = datesWithTodos;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const hasTodos = t9;
  const T0 = Button;
  const t10 = "ghost";
  const t11 = "icon";
  let t12;
  if ($[20] !== day.date) {
    t12 = day.date.toLocaleDateString();
    $[20] = day.date;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  const t13 = modifiers.selected && !modifiers.range_start && !modifiers.range_end && !modifiers.range_middle;
  const t14 = modifiers.range_start;
  const t15 = modifiers.range_end;
  const t16 = modifiers.range_middle;
  const t17 = cn("data-[selected-single=true]:bg-primary data-[selected-single=true]:text-primary-foreground data-[range-middle=true]:bg-accent data-[range-middle=true]:text-accent-foreground data-[range-start=true]:bg-primary data-[range-start=true]:text-primary-foreground data-[range-end=true]:bg-primary data-[range-end=true]:text-primary-foreground group-data-[focused=true]/day:border-ring group-data-[focused=true]/day:ring-ring/50 dark:hover:text-accent-foreground flex aspect-square size-auto w-full min-w-(--cell-size) flex-col gap-1 leading-none font-normal group-data-[focused=true]/day:relative group-data-[focused=true]/day:z-10 group-data-[focused=true]/day:ring-[3px] data-[range-end=true]:rounded-md data-[range-end=true]:rounded-r-md data-[range-middle=true]:rounded-none data-[range-start=true]:rounded-md data-[range-start=true]:rounded-l-md [&>span]:text-xs [&>span]:opacity-70", defaultClassNames.day, className);
  let t18;
  if ($[22] !== day.date) {
    t18 = day.date.getDate();
    $[22] = day.date;
    $[23] = t18;
  } else {
    t18 = $[23];
  }
  let t19;
  if ($[24] !== t18) {
    t19 = <span>{t18}</span>;
    $[24] = t18;
    $[25] = t19;
  } else {
    t19 = $[25];
  }
  let t20;
  if ($[26] !== hasTodos) {
    t20 = hasTodos && <div className="w-1 h-1 rounded-full bg-primary absolute bottom-1" />;
    $[26] = hasTodos;
    $[27] = t20;
  } else {
    t20 = $[27];
  }
  let t21;
  if ($[28] !== T0 || $[29] !== modifiers.range_end || $[30] !== modifiers.range_middle || $[31] !== modifiers.range_start || $[32] !== props || $[33] !== t12 || $[34] !== t13 || $[35] !== t17 || $[36] !== t19 || $[37] !== t20) {
    t21 = <T0 ref={ref} variant={t10} size={t11} data-day={t12} data-selected-single={t13} data-range-start={t14} data-range-end={t15} data-range-middle={t16} className={t17} {...props}>{t19}{t20}</T0>;
    $[28] = T0;
    $[29] = modifiers.range_end;
    $[30] = modifiers.range_middle;
    $[31] = modifiers.range_start;
    $[32] = props;
    $[33] = t12;
    $[34] = t13;
    $[35] = t17;
    $[36] = t19;
    $[37] = t20;
    $[38] = t21;
  } else {
    t21 = $[38];
  }
  return t21;
}
export { Calendar, CalendarDayButton };